/**
 * Map Type Toggle Plugin for Native Interactive Site Map (ISM)
 *
 * Orchestrates the Road/Satellite toggle control.
 * Delegates all DOM interactions to the mapTypeToggle component.
 *
 * Responsibilities:
 * - Initialize the map controls component
 * - Inject the component into the map
 * - Wire handlers and state management
 * - Clean up component on stop
 *
 * @module nativeInteractiveSiteMap/plugins/mapTypeToggle.plugin
 */

import { take } from 'rxjs/operators';
import MapTypes from 'enums/mapTypes';
import createMapTypeToggleDom from '../components/mapTypeToggle.dom';
import createMapViewControlsHandlers from '../handlers/mapViewControlsHandlers';

export default function createMapTypeTogglePlugin() {
  const state = {
    component: null,
    mapFacade: null,
    currentMapType: MapTypes.Roadmap,
    subscriptions: [],
  };

  return {
    name: 'map-type-toggle',
    async init(context) {
      context.state.mapTypeToggle = {
        started: false,
        ready: false,
      };
    },

    async start(context) {
      if (context.state.mapTypeToggle?.started) {
        return;
      }

      state.mapFacade = context.services.mapFacade;

      if (!state.mapFacade) {
        return;
      }

      // Set started synchronously before the async subscription
      context.state.mapTypeToggle = { started: true, ready: false };

      state.subscriptions.push(
        context.services.configuration$.pipe(take(1)).subscribe(({ resourceDomain }) => {
          const baseUrl = `${resourceDomain}/GlobalResources23/images/nativeInteractiveSiteMap`;
          // Create the component (handles all DOM internally)
          state.component = createMapTypeToggleDom(baseUrl);
    
          // Insert component into map container
          const mapElement = context.root.querySelector('[data-map-element]');
          const controlElement = state.component.getElement();
          if (mapElement && controlElement) {
            mapElement.appendChild(controlElement);
          }
    
          // Create handlers and attach to component
          const handlers = createMapViewControlsHandlers(
            state.mapFacade.setMapType,
            state.component.updateControlState
          );
    
          state.component.attachEventListeners(handlers);
    
          // Initialize control state (road selected by default)
          state.component.updateControlState(state.currentMapType);
    
          context.state.mapTypeToggle.ready = true;
        })
      );
    },

    async stop(context) {
      state.subscriptions.forEach(s => s?.unsubscribe?.());
      state.subscriptions = [];
      state.component?.remove();
      state.component = null;
      state.mapFacade = null;
      // Reset to neutral shape, matching what init() created
      context.state.mapTypeToggle = { started: false, ready: false };
    },
  };
}